/**
 * The HHAeXchange upload report for one visit: one line for every item the
 * agency configured this visit to upload, whatever happened to it.
 *
 * Task Health (2026-10-26): the Admin Hub shows every line (uploaded, partly
 * uploaded with how many, failed, skipped, still running, not started) and
 * whose it is to resolve; the Agency Portal shows only what is partly
 * uploaded or failed, each marked as Task Health's to resolve or the
 * agency's, and never what was skipped.
 *
 * Pure: the API loads the configured items and the ledger rows
 * (`hhax_push_status`) and calls `buildUploadReport`; both apps read the same
 * shape.
 */
export const HHAX_UPLOAD_LABELS = {
    create_visit: "Create the visit",
    upload_supervisory: "Supervisory evaluation",
    upload_annual_eval: "Annual / performance evaluation",
    upload_initial_assessment: "Initial assessment",
    upload_485: "485 / MD order",
    upload_signed_485: "Signed 485",
    upload_poc_doc: "Plan of care",
    upload_emergency_kardex: "Emergency kardex",
    upload_welcome_package: "Welcome package",
    upload_caregiver_supervision: "Caregiver supervision document",
    push_certification: "Certification period",
    push_poc_duties: "Plan-of-care duties",
    push_diagnoses: "Diagnoses",
    push_allergies: "Allergies",
    push_medications: "Medications",
    push_physician: "Physician",
    push_emergency_prep: "Emergency preparedness",
    push_485_details: "MD order details"
};
export const hhaxUploadLabel = (capability) => HHAX_UPLOAD_LABELS[capability] ?? capability.replace(/_/g, " ");
/** Older RPA rows carry their count only as text: "(12 of 15 medications filed)". */
export function countsFromRef(ref) {
    const m = ref?.match(/\((\d+) of (\d+)\b/);
    return m ? { done: Number(m[1]), total: Number(m[2]) } : null;
}
function stateOf(row) {
    switch (row.status) {
        case "pushed":
            return row.dryRun ? "not_started" : "uploaded";
        case "partial":
            return "partial";
        case "skipped":
            return "skipped";
        case "failed":
            // "Nothing of this kind to send" was recorded as a failure by older writers.
            return row.failureClass === "not-applicable" || row.failureClass === "routed-elsewhere"
                ? "skipped"
                : "failed";
        case "queued":
        case "in_progress":
            return "in_progress";
        default:
            return "not_started";
    }
}
/** Only the agency's own action makes it the agency's; the nurse, vendor and us are Task Health's. */
function ownerOf(blockedOn) {
    return blockedOn === "agency" ? "agency" : "taskhealth";
}
export const UPLOAD_NOT_REPORTED = "The upload finished without reporting this item, so it may not be in HHAeXchange.";
/**
 * One line per configured item, plus any item uploaded before it was switched
 * off. A configured item with no ledger row reads "not started" until the
 * visit has been uploaded (some live row exists and nothing is running); after
 * that it is a failure on Task Health, because our upload skipped it silently.
 */
export function buildUploadReport(input) {
    const byCap = new Map(input.rows.map((r) => [r.capability, r]));
    const configured = new Set(input.configured);
    const live = input.rows.filter((r) => !r.dryRun);
    const running = input.rows.some((r) => r.status === "queued" || r.status === "in_progress");
    const uploadedOnce = live.length > 0 && !running;
    const caps = [...new Set([...input.configured, ...input.rows.map((r) => r.capability)])];
    const rank = (c) => {
        const i = input.order?.indexOf(c) ?? -1;
        return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    caps.sort((a, b) => rank(a) - rank(b));
    const items = caps.map((capability) => {
        const row = byCap.get(capability);
        const isConfigured = configured.has(capability);
        if (!row) {
            const missed = uploadedOnce;
            return {
                capability,
                label: hhaxUploadLabel(capability),
                state: missed ? "failed" : "not_started",
                done: null,
                total: null,
                owner: missed ? "taskhealth" : null,
                ownerDetail: missed ? "us" : null,
                message: missed ? UPLOAD_NOT_REPORTED : null,
                agencyMessage: missed ? UPLOAD_NOT_REPORTED : null,
                hhaxRef: null,
                configured: isConfigured,
                preview: false,
                attempts: 0,
                updatedAt: null
            };
        }
        const state = stateOf(row);
        const open = state === "partial" || state === "failed";
        const parsed = countsFromRef(row.externalRef);
        return {
            capability,
            label: hhaxUploadLabel(capability),
            state,
            done: row.doneCount ?? parsed?.done ?? null,
            total: row.totalCount ?? parsed?.total ?? null,
            owner: open ? ownerOf(row.blockedOn) : null,
            ownerDetail: open ? (row.blockedOn ?? "us") : null,
            message: row.errorMessage,
            agencyMessage: open ? row.agencyMessage : null,
            hhaxRef: row.externalRef,
            configured: isConfigured,
            preview: row.dryRun,
            attempts: row.attemptNumber,
            updatedAt: row.updatedAt instanceof Date ? row.updatedAt.toISOString() : row.updatedAt
        };
    });
    return { visitId: input.visitId, items, summary: summarise(items, configured.size) };
}
function summarise(items, configured) {
    const count = (state) => items.filter((i) => i.state === state).length;
    return {
        uploaded: count("uploaded"),
        partial: count("partial"),
        failed: count("failed"),
        skipped: count("skipped"),
        in_progress: count("in_progress"),
        not_started: count("not_started"),
        configured,
        onTaskHealth: items.filter((i) => i.owner === "taskhealth").length,
        onAgency: items.filter((i) => i.owner === "agency").length
    };
}
/**
 * What the agency sees: partial and failed items only, never skipped, with
 * Task Health's internal message removed (the agency reads `agencyMessage`).
 * The summary keeps the counts the agency may see.
 */
export function uploadReportForAgency(report) {
    const items = report.items
        .filter((i) => i.state === "partial" || i.state === "failed")
        .map((i) => ({ ...i, message: null, ownerDetail: null }));
    return {
        visitId: report.visitId,
        items,
        summary: { ...report.summary, skipped: 0 }
    };
}
//# sourceMappingURL=hhax-upload-report.js.map